/**
 * Low-level HTTP layer: one fetch wrapper that attaches the JWT, parses the
 * `{ success, data }` envelope through a Zod schema, maps failures to ApiError,
 * and transparently refreshes the access token once on a 401.
 */
import type { z } from 'zod';
import { env } from '@/shared/config/env';
import { ApiError, mapHttpError } from './errors';
import { tokenStore } from './token-store';
import { notifySessionExpired } from './session';
import { envelope, RefreshResponseSchema } from '@/shared/types/schemas';

type Query = Record<string, string | number | undefined>;

interface RequestOptions<T extends z.ZodTypeAny> {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  path: string;
  schema: T;
  body?: unknown;
  query?: Query;
  /** Skip auth header + refresh (used by login itself). */
  anonymous?: boolean;
  /** Send x-admin-token instead of Bearer JWT (for /admin/* routes). */
  adminToken?: boolean;
  signal?: AbortSignal;
}

function buildUrl(path: string, query?: Query): string {
  const url = `${env.VITE_API_BASE_URL}${path}`;
  if (!query) return url;
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) {
    if (v !== undefined && v !== '') params.set(k, String(v));
  }
  const qs = params.toString();
  return qs ? `${url}?${qs}` : url;
}

let refreshInFlight: Promise<boolean> | null = null;

/** Attempt a single token refresh; dedupes concurrent callers. */
async function tryRefresh(): Promise<boolean> {
  if (refreshInFlight) return refreshInFlight;
  const refreshToken = tokenStore.getRefresh();
  if (!refreshToken) return false;

  refreshInFlight = (async () => {
    try {
      const res = await fetch(buildUrl('/auth/refresh'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken }),
      });
      if (!res.ok) return false;
      const json = await res.json();
      const parsed = envelope(RefreshResponseSchema).safeParse(json);
      if (!parsed.success) return false;
      tokenStore.setAccess(parsed.data.data.token);
      return true;
    } catch {
      return false;
    } finally {
      refreshInFlight = null;
    }
  })();

  return refreshInFlight;
}

/** Read the admin token from environment (set in .env.local as VITE_ADMIN_TOKEN). */
function getAdminToken(): string {
  return (import.meta as unknown as { env: Record<string, string> }).env.VITE_ADMIN_TOKEN ?? '';
}

async function doFetch<T extends z.ZodTypeAny>(
  opts: RequestOptions<T>,
  isRetry: boolean,
): Promise<z.infer<T>> {
  const { method = 'GET', path, schema, body, query, anonymous, adminToken, signal } = opts;

  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (!anonymous) {
    // Admin routes are authorized by the super-admin JWT — same Bearer as the
    // rest of the dashboard, so no shared secret is shipped to the browser.
    const token = tokenStore.getAccess();
    if (token) headers.Authorization = `Bearer ${token}`;
    // Optional fallback for non-browser callers that configure a shared token.
    if (adminToken) {
      const at = getAdminToken();
      if (at) headers['x-admin-token'] = at;
    }
  }

  let res: Response;
  try {
    res = await fetch(buildUrl(path, query), {
      method,
      headers,
      body: body !== undefined ? JSON.stringify(body) : undefined,
      signal,
    });
  } catch (e) {
    if (signal?.aborted) throw e;
    throw new ApiError('network', 'Could not reach the analytics service.', undefined, e);
  }

  if (res.status === 401 && !anonymous && !isRetry) {
    const refreshed = await tryRefresh();
    if (refreshed) return doFetch(opts, true);
    // Token is missing/expired and can't be refreshed → end the session and
    // bounce to /login instead of leaving the app showing "Missing bearer token".
    notifySessionExpired();
  }

  if (!res.ok) {
    let payload: unknown = undefined;
    try {
      payload = await res.json();
    } catch {
      /* non-JSON error body */
    }
    throw mapHttpError(res.status, payload);
  }

  const json = await res.json();
  const parsed = envelope(schema).safeParse(json);
  if (!parsed.success) {
    throw new ApiError(
      'validation',
      'Unexpected response from the analytics service.',
      res.status,
      parsed.error.issues,
    );
  }
  return parsed.data.data;
}

/** Typed GET request returning the unwrapped, schema-validated `data`. */
export function request<T extends z.ZodTypeAny>(opts: RequestOptions<T>): Promise<z.infer<T>> {
  return doFetch(opts, false);
}

/** Typed mutation (POST/PUT/PATCH/DELETE) — same contract, different method default. */
export function requestMutation<T extends z.ZodTypeAny>(
  opts: Omit<RequestOptions<T>, 'method'> & { method: 'POST' | 'PUT' | 'PATCH' | 'DELETE' },
): Promise<z.infer<T>> {
  return doFetch(opts, false);
}
